import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

const AppContext = createContext();

// Storage keys
const STORAGE_KEYS = {
  MOCK_MODE: 'stripe_mock_mode',
  UPLOAD_QUEUE: 'document_upload_queue',
  CURRENT_VISA_TYPE: 'current_visa_type',
  QUEUE_SESSION_ID: 'queue_session_id'
};

// Generate a unique session ID for this browser session
const generateSessionId = () => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Pricing tiers
const PRICING = {
  PER_DOCUMENT: 10,
  TIERS: [
    { min: 1, max: 3, pricePerDoc: 10, discount: 0 },
    { min: 4, max: 6, pricePerDoc: 8, discount: 20 },
    { min: 7, max: Infinity, pricePerDoc: 7, discount: 30 }
  ]
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  // Stripe mock mode
  const [stripeMockMode, setStripeMockMode] = useState(() => {
    return localStorage.getItem(STORAGE_KEYS.MOCK_MODE) === 'true';
  });

  // Auth state - user data from Google OAuth
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  // Document upload queue
  const [uploadQueue, setUploadQueue] = useState([]);

  // Current visa type being worked on
  const [currentVisaType, setCurrentVisaType] = useState(null);

  // Session ID for queue management
  const [queueSessionId, setQueueSessionId] = useState(null);

  // Update localStorage when stripe mock mode changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.MOCK_MODE, stripeMockMode.toString());
  }, [stripeMockMode]);

  // Persist upload queue to localStorage
  useEffect(() => {
    try {
      if (uploadQueue.length > 0 && queueSessionId) {
        localStorage.setItem(STORAGE_KEYS.UPLOAD_QUEUE, JSON.stringify(uploadQueue));
        localStorage.setItem(STORAGE_KEYS.QUEUE_SESSION_ID, queueSessionId);
      } else {
        localStorage.removeItem(STORAGE_KEYS.UPLOAD_QUEUE);
        localStorage.removeItem(STORAGE_KEYS.QUEUE_SESSION_ID);
      }
    } catch (e) {
      console.warn('Could not save upload queue to localStorage');
    }
  }, [uploadQueue, queueSessionId]);

  // Persist current visa type
  useEffect(() => {
    if (currentVisaType) {
      localStorage.setItem(STORAGE_KEYS.CURRENT_VISA_TYPE, currentVisaType);
    } else {
      localStorage.removeItem(STORAGE_KEYS.CURRENT_VISA_TYPE);
    }
  }, [currentVisaType]);

  // Check authentication on mount
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  useEffect(() => {
    // Skip auth check if returning from OAuth callback
    if (window.location.hash?.includes('session_id=')) {
      setAuthLoading(false);
      return;
    }
    
    const checkAuth = async () => {
      try {
        const apiUrl = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${apiUrl}/api/auth/me`, {
          credentials: 'include'
        });
        
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
        }
      } catch (error) {
        // Not authenticated - that's fine
        console.log('Not authenticated');
      } finally {
        setAuthLoading(false);
      }
    };
    
    checkAuth();
  }, []);

  const toggleStripeMock = useCallback(() => {
    setStripeMockMode(prev => !prev);
  }, []);

  // Login redirect - uses Emergent Auth
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogle = useCallback(() => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  // Logout
  const logout = useCallback(async () => {
    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${apiUrl}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
    }
  }, []);

  // Set user (called from AuthCallback)
  const setAuthUser = useCallback((userData) => {
    setUser(userData);
    setAuthLoading(false);
  }, []);

  // Start a new upload session - clears any existing queue
  const startNewSession = useCallback(() => {
    const newSessionId = generateSessionId();
    setQueueSessionId(newSessionId);
    setUploadQueue([]);
    setCurrentVisaType(null);
    
    // Clear all queue-related localStorage
    localStorage.removeItem(STORAGE_KEYS.UPLOAD_QUEUE);
    localStorage.removeItem(STORAGE_KEYS.QUEUE_SESSION_ID);
    localStorage.removeItem(STORAGE_KEYS.CURRENT_VISA_TYPE);
    localStorage.removeItem('pending_document_ids');
    localStorage.removeItem('batch_visa_type');
    localStorage.removeItem('pending_document_id');
    
    return newSessionId;
  }, []);

  // Add document to upload queue (supports multiple items per document type for bank statements)
  const addToQueue = useCallback((document) => {
    // Ensure we have a session
    if (!queueSessionId) {
      const newSessionId = generateSessionId();
      setQueueSessionId(newSessionId);
    }
    
    setUploadQueue(prev => {
      // For documents with monthIndex (bank statements), allow multiple
      if (document.monthIndex !== undefined && document.monthIndex !== null) {
        return [...prev, {
          ...document,
          addedAt: new Date().toISOString()
        }];
      }
      
      // For regular documents, check for duplicates
      const existingIndex = prev.findIndex(d => d.documentType === document.documentType);
      if (existingIndex >= 0) {
        // Return special flag to indicate duplicate
        return { ...prev, _duplicateFound: true, _existingDoc: prev[existingIndex], _newDoc: document };
      }
      return [...prev, {
        ...document,
        addedAt: new Date().toISOString()
      }];
    });
  }, [queueSessionId]);

  // Replace document in queue (for duplicate handling)
  const replaceInQueue = useCallback((documentType, newDocument) => {
    setUploadQueue(prev => {
      const filtered = prev.filter(d => d.documentType !== documentType);
      return [...filtered, {
        ...newDocument,
        addedAt: new Date().toISOString()
      }];
    });
  }, []);

  // Remove document from queue
  const removeFromQueue = useCallback((pendingId) => {
    setUploadQueue(prev => prev.filter(d => d.pendingId !== pendingId));
  }, []);

  // Clear entire queue
  const clearQueue = useCallback(() => {
    setUploadQueue([]);
    setQueueSessionId(null);
    setCurrentVisaType(null);
    
    // Clear all queue-related localStorage
    localStorage.removeItem(STORAGE_KEYS.UPLOAD_QUEUE);
    localStorage.removeItem(STORAGE_KEYS.QUEUE_SESSION_ID);
    localStorage.removeItem(STORAGE_KEYS.CURRENT_VISA_TYPE);
    localStorage.removeItem('pending_document_ids');
    localStorage.removeItem('batch_visa_type');
    localStorage.removeItem('pending_document_id');
  }, []);

  // Check if document type is in queue
  const isInQueue = useCallback((documentType) => {
    return uploadQueue.some(d => d.documentType === documentType || d.documentType?.startsWith(documentType));
  }, [uploadQueue]);

  // Get document from queue by type
  const getFromQueue = useCallback((documentType) => {
    return uploadQueue.find(d => d.documentType === documentType || d.documentType?.startsWith(documentType));
  }, [uploadQueue]);

  // Get all queue items for a document type (for multi-upload like bank statements)
  const getQueueItemsByType = useCallback((documentType) => {
    return uploadQueue.filter(d => 
      d.documentType === documentType || 
      d.documentType?.startsWith(documentType) ||
      d.selectedDocument?.name === documentType
    );
  }, [uploadQueue]);

  // Calculate pricing based on queue size
  const queuePricing = useMemo(() => {
    const count = uploadQueue.length;
    if (count === 0) {
      return { total: 0, perDocument: PRICING.PER_DOCUMENT, discount: 0, savings: 0 };
    }

    const tier = PRICING.TIERS.find(t => count >= t.min && count <= t.max);
    const total = count * tier.pricePerDoc;
    const originalTotal = count * PRICING.PER_DOCUMENT;
    const savings = originalTotal - total;

    return {
      total,
      perDocument: tier.pricePerDoc,
      discount: tier.discount,
      savings,
      documentCount: count
    };
  }, [uploadQueue]);

  // Get all pending IDs for batch processing
  const getPendingIds = useCallback(() => {
    return uploadQueue.map(d => d.pendingId);
  }, [uploadQueue]);

  const value = {
    // Stripe mock mode
    stripeMockMode,
    setStripeMockMode,
    toggleStripeMock,
    
    // Auth
    user,
    authLoading,
    loginWithGoogle,
    logout,
    setAuthUser,
    isAuthenticated: !!user,
    
    // Document queue
    uploadQueue,
    addToQueue,
    replaceInQueue,
    removeFromQueue,
    clearQueue,
    isInQueue,
    getFromQueue,
    getQueueItemsByType,
    queuePricing,
    getPendingIds,
    startNewSession,
    queueSessionId,
    
    // Visa type context
    currentVisaType,
    setCurrentVisaType
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
